import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import { Link } from 'react-router-dom'
import { ALL_COURSES } from '../../gql/queries/academyQuery'
import { IAllCoursesResponse } from '../../types/academy'
import { prependBackendUrl } from '../../gql/client'
import { useGetUser } from '../../store/userStore'
import { hasPermission } from '../../helpers'
import { ALL_PERMISSIONS } from '../../helpers/consts'
import { useIntl } from 'react-intl'
import { ACADEMY_MESSAGES as A, PERCENT } from './academyMessages'

export const AcademyPage = () => {
  const intl = useIntl()
  const permissions = useGetUser((s) => s.user?.permissions)
  const canManage = hasPermission(permissions || [], ALL_PERMISSIONS.ADD_COURSE)

  const [filterName, setFilterName] = useState('')
  const [debouncedFilterName, setDebouncedFilterName] = useState('')

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFilterName(filterName)
    }, 500)
    return () => clearTimeout(handler)
  }, [filterName])

  const { data, loading, error } = useQuery<IAllCoursesResponse>(ALL_COURSES, {
    variables: {
      isActive: true,
      name: debouncedFilterName || null,
    },
    fetchPolicy: 'network-only',
  })

  const courses = useMemo(() => data?.allCourses || [], [data])

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {intl.formatMessage(A.errLoadingCourses, {message: error.message})}
      </div>
    )
  }

  return (
    <div className="container-fluid px-4 py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="h3 mb-1">{intl.formatMessage(A.title)}</h1>
          <p className="text-muted mb-0">{intl.formatMessage(A.subtitle)}</p>
        </div>
        {canManage && (
          <Link to="/academy/manage" className="btn btn-primary btn-sm">
            <i className="bi bi-gear me-1" /> {intl.formatMessage(A.manageCourses)}
          </Link>
        )}
      </div>

      {/* Search */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-6 col-lg-4">
              <label className="form-label fw-bold">{intl.formatMessage(A.searchByName)}</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  className="form-control"
                  placeholder={intl.formatMessage(A.searchPlaceholder)}
                  value={filterName}
                  onChange={(e) => setFilterName(e.target.value)}
                />
                <span style={{ position: 'absolute', right: '10px', top: '8px' }}>
                  <i className="bi bi-search" />
                </span>
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-8 text-md-end text-muted small">
              {intl.formatMessage(A.courseCount, {count: courses.length})}
            </div>
          </div>
        </div>
      </div>

      {/* Course cards */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{intl.formatMessage(A.loading)}</span>
          </div>
        </div>
      ) : courses.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-5">
            <i className="bi bi-mortarboard" style={{ fontSize: 48, color: '#ccc' }} />
            <h5 className="mt-3 text-muted">{intl.formatMessage(A.noCourses)}</h5>
            <p className="text-muted">
              {intl.formatMessage(canManage ? A.noCoursesManager : A.noCoursesUser)}
            </p>
          </div>
        </div>
      ) : (
        <div className="row g-3">
          {courses.map((course) => (
            <div key={course.id} className="col-md-6 col-lg-4 col-xl-3">
              <div className="card h-100 shadow-sm hover-shadow">
                {/* Cover */}
                <div
                  style={{
                    height: 160,
                    backgroundColor: '#f8f9fa',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  }}
                >
                  {course.coverImage?.file ? (
                    <img
                      src={prependBackendUrl(course.coverImage.file)}
                      alt={course.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <i className="bi bi-mortarboard" style={{ fontSize: 56, color: '#0d6efd' }} />
                  )}
                </div>

                <div className="card-body d-flex flex-column">
                  <h6 className="card-title mb-2">{course.name}</h6>
                  {course.description && (
                    <p
                      className="card-text text-muted small"
                      style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {course.description}
                    </p>
                  )}

                  <div className="mt-auto">
                    <div className="d-flex justify-content-between text-muted small mb-1">
                      <span>
                        <i className="bi bi-collection-play me-1" />
                        {intl.formatMessage(A.lessonCount, {count: course.lessonsCount})}
                      </span>
                      <span>{course.completionPercentage}{PERCENT}</span>
                    </div>
                    <div className="progress mb-3" style={{ height: 6 }}>
                      <div
                        className={`progress-bar ${
                          course.completionPercentage === 100 ? 'bg-success' : 'bg-primary'
                        }`}
                        role="progressbar"
                        style={{ width: `${course.completionPercentage}%` }}
                        aria-valuenow={course.completionPercentage}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      />
                    </div>
                    <Link to={`/academy/${course.id}`} className="btn btn-sm btn-primary w-100">
                      {course.completionPercentage === 100 ? (
                        <>
                          <i className="bi bi-check-circle me-1" />{' '}
                          {intl.formatMessage(A.reviewCourse)}
                        </>
                      ) : course.completionPercentage > 0 ? (
                        <>
                          <i className="bi bi-play-circle me-1" />{' '}
                          {intl.formatMessage(A.continueCourse)}
                        </>
                      ) : (
                        <>
                          <i className="bi bi-play-circle me-1" />{' '}
                          {intl.formatMessage(A.startCourse)}
                        </>
                      )}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
